import React from "react";
import { Card, Box, Typography, Skeleton } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

/**
 * Compact metric tile for dashboards. `trend` is a signed number rendered as
 * a coloured delta; omit it for a plain value.
 *
 * `color` is a palette key with main + soft (primary, success, warning, error,
 * violet, teal). The tile takes the soft tint as its surface, so a row of
 * tiles reads as a row of distinct colours rather than four white boxes.
 */
export default function StatCard({
  label,
  value,
  unit,
  icon: Icon,
  trend,
  trendLabel,
  color = "primary",
  loading = false,
}) {
  const isUp = typeof trend === "number" && trend >= 0;
  const TrendIcon = isUp ? TrendingUpIcon : TrendingDownIcon;

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 2.5 },
        height: "100%",
        bgcolor: `${color}.soft`,
        borderColor: "transparent",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        {Icon && (
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: `${color}.main`,
              color: "common.white",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 19 }} />
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 1.5, display: "flex", alignItems: "baseline", gap: 0.75 }}>
        {loading ? (
          <Skeleton width={90} height={38} />
        ) : (
          <>
            <Typography
              sx={{
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 1.1,
              }}
            >
              {value}
            </Typography>
            {unit && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {unit}
              </Typography>
            )}
          </>
        )}
      </Box>

      {!loading && typeof trend === "number" && (
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
          <TrendIcon sx={{ fontSize: 16, color: isUp ? "success.main" : "error.main" }} />
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: isUp ? "success.main" : "error.main" }}
          >
            {isUp ? "+" : ""}
            {trend}%
          </Typography>
          {trendLabel && (
            <Typography variant="caption" color="text.secondary">
              {trendLabel}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
}
